import * as React from 'react';
import { cn } from '../../lib/utils';
import type { InputProps } from './input';

const UAE_PREFIX = '+971';

export interface IdentifierInputProps
  extends Omit<InputProps, 'value' | 'onChange' | 'defaultValue'> {
  /** Called with the effective value to submit: "+971<digits>" in phone
   * mode, otherwise the raw typed text unchanged. */
  onValueChange: (value: string) => void;
}

/**
 * Login "email, username, or phone number" field. Auto-detects phone
 * entry: if the first character typed is a digit, shows a fixed "+971"
 * prefix and restricts further input to digits (max 9, the UAE mobile
 * subscriber number) — matching the canonical format every other phone
 * entry point in the app now locks to. Any non-digit first character
 * leaves it a plain, unrestricted text field for email/username.
 */
export const IdentifierInput = React.forwardRef<HTMLInputElement, IdentifierInputProps>(
  ({ onValueChange, hasError, className, placeholder, ...props }, ref) => {
    const [raw, setRaw] = React.useState('');
    const isPhoneMode = raw.length > 0 && /^\d/.test(raw[0]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const phoneMode = value.length > 0 && /^\d/.test(value[0]);
      const nextRaw = phoneMode ? value.replace(/\D/g, '').slice(0, 9) : value;
      setRaw(nextRaw);
      onValueChange(phoneMode ? `${UAE_PREFIX}${nextRaw}` : nextRaw);
    };

    if (!isPhoneMode) {
      return (
        <input
          ref={ref}
          type="text"
          value={raw}
          onChange={handleChange}
          placeholder={placeholder}
          className={cn(
            'w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-gray-900 transition-all',
            'placeholder:text-gray-400 focus:outline-none focus:ring-2',
            hasError
              ? 'border-error-400 focus:border-error-500 focus:ring-error-100'
              : 'border-gray-300 hover:border-gray-400 focus:border-brand-400 focus:ring-brand-100',
            className,
          )}
          {...props}
        />
      );
    }

    return (
      <div
        className={cn(
          'flex items-center rounded-xl border bg-white transition-all focus-within:ring-2',
          hasError
            ? 'border-error-400 focus-within:border-error-500 focus-within:ring-error-100'
            : 'border-gray-300 hover:border-gray-400 focus-within:border-brand-400 focus-within:ring-brand-100',
          className,
        )}
      >
        <span className="pl-3.5 py-2.5 text-sm text-gray-500 select-none">{UAE_PREFIX}</span>
        <input
          ref={ref}
          type="tel"
          inputMode="numeric"
          maxLength={9}
          value={raw}
          onChange={handleChange}
          placeholder="502305316"
          className="w-full rounded-r-xl bg-transparent py-2.5 pr-3.5 pl-1 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
          {...props}
        />
      </div>
    );
  },
);
IdentifierInput.displayName = 'IdentifierInput';
